import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import api from '../../axios/api';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    Box,
    TextField,
    InputAdornment,
    Typography,
    Alert,
    AlertTitle
} from '@mui/material';
import CustomButton from '../../componentsII/Buttons';
import { PenBoxIcon, SearchIcon, X } from 'lucide-react';
import { useSelector } from 'react-redux';
import { showNotification } from "../../redux/slices/notificationSlice";

const AddEditSources = ({ open, handleClose, mode = 'add', id = 0 }) => {
    const dispatch = useDispatch();
    const [sources, setSources] = useState([]);
    const [selectedSources, setSelectedSources] = useState([]);
    const [initialSelectedSources, setInitialSelectedSources] = useState([]); // New state for initial selected
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [sourcesToConfirm, setSourcesToConfirm] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const user = useSelector((state) => state.user);
    const selectedYear = useSelector((state) => state.user.selectedYear);

    const filteredSources = sources.filter(source =>
        source.cat_title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getSources = async () => {
        try {
            const res = await api.get('/common/scope3Sources');
            setSources(res.data.data);
        } catch (error) {
            dispatch(showNotification({
                message: "Failed to fetch sources",
                type: "error"
            }));
        }
    };

    const getSelectedSources = async () => {
        try {
            const res = await api.get(`/company/getScope3Selection?company_id=${user.user.company.id}&comp_data_year_id=${selectedYear.id}`);
            const data = res.data.data;
            const formattedSources = data.map((item) => ({
                ...item.categories,
                review_done: item.review_done,
                send_for_review: item.send_for_review
            }));
            setSelectedSources(formattedSources);
            setInitialSelectedSources(formattedSources); // Store initial state
        } catch (error) {
            dispatch(showNotification({
                message: "Error fetching selected sources",
                type: "error"
            }));
        }
    };

    useEffect(() => {
        getSources();
            getSelectedSources();
    }, []);

    const handleSourceToggle = (source) => {
        setSelectedSources(prev => {
            const isCurrentlySelected = prev.some(s => s.id === source.id);
            if (isCurrentlySelected) {
                return prev.filter(s => s.id !== source.id);
            } else {
                return [...prev, source];
            }
        });
    };

    const handleSave = () => {
        // Compare current selection with initial selection
        const sourcesNeedingConfirmation = initialSelectedSources.filter(initialSource => {
            const isNowUnselected = !selectedSources.some(s => s.id === initialSource.id);
            return isNowUnselected;
        });

        if (sourcesNeedingConfirmation.length > 0) {
            setSourcesToConfirm(sourcesNeedingConfirmation);
            setShowConfirmDialog(true);
        } else {
            saveChanges();
        }
    };

    const saveChanges = async () => {
        try {
            const selectedSourceIds = selectedSources.map(source => source.id);
            const payload = {
                user_id: user.user.id,
                id,
                company_id: user.user.company.id,
                comp_data_year_id: user.selectedYear.id,
                selected_source_ids: selectedSourceIds
            };

            const res = await api.post("/company/AddUpdateSelectedSources", payload);

            if (res.data.status) {
                dispatch(showNotification({
                    message: res.data.message || "Sources updated successfully",
                    type: "success"
                }));
                handleClose();
            } else {
                dispatch(showNotification({
                    message: res.data.message || "Error saving sources",
                    type: "error"
                }));
            }
        } catch (error) {
            dispatch(showNotification({
                message: "Failed to save sources",
                type: "error"
            }));
        }
    };

    const handleConfirmRemoval = () => {
        saveChanges();
        setShowConfirmDialog(false);
        setSourcesToConfirm([]);
    };

    const handleCancel = () => {
        setSelectedSources(initialSelectedSources);
        handleClose();
    };

    return (
        <>
            <Dialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Removal</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        <AlertTitle>
                            Warning
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
                                aria-label="Close"
                            >
                                <X size={24} />
                            </button>
                        </AlertTitle>
                        <div className='m-2'>
                            The emission sources you have removed have associated files.If you continue,the files and any associated transactions will be deleted.
                            You will need to resubmit the file again if you want to re-add these sources later. Do you want to continue?
                            <ul className="mt-2 list-disc pl-5">
                                {sourcesToConfirm.map(source => (
                                    <li key={source.id}>{source.cat_title}</li>
                                ))}
                            </ul>
                        </div>
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <CustomButton
                        className="bg-gray-500 hover:bg-gray-700 text-white"
                        click={() => setShowConfirmDialog(false)}
                        text="Cancel"
                    />
                    <CustomButton
                        className="bg-red-600 hover:bg-red-700 text-white"
                        click={handleConfirmRemoval}
                        text="Continue"
                    />
                </DialogActions>
            </Dialog>

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle className=''>
                    <div className='flex gap-3 mt-2'>
                        {mode === 'add' ? null : <PenBoxIcon className='mt-1' />}
                        {mode === 'add' ? '+ Add Sources' : 'Edit Sources'}
                    </div>
                    <button
                        onClick={handleCancel}
                        className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
                        aria-label="Close"
                    >
                        <X size={24} />
                    </button>
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ mt: 1, height: '600px', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                        <hr className='border-gray-900 mb-4' />
                        <p className='ml-2'>Select which sources would you like to be tracking for your scope 3 footprint</p>
                        <p className='ml-2'>Note: If you remove a source that already has data associated with it, that data will be saved and can be retrieved by adding the source back</p>

                        <TextField
                            variant="outlined"
                            fullWidth
                            placeholder="Search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon size={18} />
                                    </InputAdornment>
                                ),
                            }}
                            style={{ width: '50%' }}
                            size='small'
                            sx={{ m: 2 }}
                        />

                        {filteredSources.map((source) => {
                            const isSelected = selectedSources.some(s => s.id === source.id);

                            return (
                                <FormControlLabel
                                    key={source.id}
                                    control={
                                        <Checkbox
                                            color="success"
                                            checked={isSelected}
                                            onChange={() => handleSourceToggle(source)}
                                        />
                                    }
                                    label={
                                        <div className='m-2'>
                                            <Typography variant="body1" className='m-0'>
                                                {source.cat_title}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" className='m-0'>
                                                {source.cat_no}
                                            </Typography>
                                        </div>
                                    }
                                    sx={{
                                        mb: 1,
                                        ml: 1,
                                        px: 1,
                                        borderRadius: 1,
                                        transition: "background-color 0.2s ease-in-out",
                                        backgroundColor: isSelected ? "#F0FDF4" : "transparent",
                                        "&:hover": {
                                            backgroundColor: "#F0FDF4",
                                        },
                                    }}
                                />
                            );
                        })}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <div className="w-full flex justify-end gap-5 items-center p-5">
                        <CustomButton
                            className="bg-gray-500 hover:bg-gray-700 text-white"
                            click={handleCancel}
                            text="Cancel"
                        />
                        <CustomButton
                            className="bg-green-600 hover:bg-green-700 text-white"
                            text="Submit"
                            click={handleSave}
                        />
                    </div>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AddEditSources;